// UI primitives
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Icons
import { MinusIcon } from '@/react/components/icons/MinusIcon';
import { StepperPlusIcon } from '@/react/components/icons/StepperPlusIcon';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

import { FIELD_SURFACE_BORDERED } from "@/components/ui/field-surface";
interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  invalid?: boolean;
  'aria-label'?: string;
}

// Zero is the "unset" sentinel for the counts this stepper drives, so it renders
// as an empty input behind a real `placeholder` rather than as a literal "0" the
// user has to backspace. Same convention as ServingLayoutInput's Lines/Ways.
const toDisplay = (n: number) => (n === 0 ? '' : String(n));

export function NumberStepper({
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  className,
  invalid = false,
  'aria-label': ariaLabel,
}: NumberStepperProps) {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  const [inputValue, setInputValue] = useState(toDisplay(value));

  // Sync when value changes externally (e.g. stepper buttons)
  useEffect(() => {
    setInputValue(toDisplay(value));
  }, [value]);

  const decrement = () => onChange(clamp(value - step));

  const increment = () => onChange(clamp(value + step));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Digits only, strip leading zeros (e.g. "0100" → "100")
    const raw = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
    setInputValue(raw);
    if (raw !== '') onChange(clamp(parseInt(raw, 10)));
  };

  const handleBlur = () => {
    const parsedNumber = inputValue === '' ? min : clamp(parseInt(inputValue, 10));
    onChange(parsedNumber);
    setInputValue(toDisplay(parsedNumber));
  };

  return (
    <div
      role="group"
      aria-label="Number stepper"
      aria-invalid={invalid || undefined}
      className={cn(
        cn(FIELD_SURFACE_BORDERED, 'flex items-stretch py-4'),
        invalid && 'border-chilli-red-light bg-chilli-blush',
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={decrement}
        disabled={value <= min}
        aria-label="Decrease value"
        className="group/stepper flex shrink-0 items-center rounded-l-input rounded-r-none -my-4 px-6 py-4 text-off-black transition-[background-color] hover:bg-dinnerplate-white-light disabled:opacity-30"
      >
        <MinusIcon className="transition-transform group-active/stepper:scale-90" />
      </Button>
      <Input
        type="text"
        inputMode="numeric"
        aria-label={ariaLabel}
        placeholder="0"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn(
          'h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-center font-inter text-sm tabular-nums shadow-none ring-0 focus-visible:ring-0',
          // The field is centre-aligned, so an empty input puts the caret exactly
          // where the placeholder glyph sits. Hide the placeholder while focused.
          'placeholder:text-grey-light focus:placeholder:text-transparent',
          value > min ? 'text-off-black' : 'text-grey-light',
        )}
      />
      <Button
        type="button"
        variant="ghost"
        onClick={increment}
        disabled={value >= max}
        aria-label="Increase value"
        className="group/stepper flex shrink-0 items-center rounded-l-none rounded-r-input -my-4 px-6 py-4 text-off-black transition-[background-color] hover:bg-dinnerplate-white-light disabled:opacity-30"
      >
        <StepperPlusIcon className="transition-transform group-active/stepper:scale-90" />
      </Button>
    </div>
  );
}
